import { HttpBackend, HttpClient, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, tap, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

/** Reachability, not authentication: never classify a 4xx response as lost internet. */
@Injectable({ providedIn: 'root' })
export class ConnectionStateService {
  readonly state$ = new BehaviorSubject<'online' | 'offline' | 'unreachable'>(navigator.onLine ? 'online' : 'offline');
  readonly recovered$ = new Subject<void>();
  private readonly http: HttpClient;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private checking = false;
  private generation = 0;
  constructor(backend: HttpBackend, private zone: NgZone) {
    this.http = new HttpClient(backend);
    window.addEventListener('offline', () => this.update('offline'));
    window.addEventListener('online', () => this.check());
    window.addEventListener('pageshow', () => { if (this.state$.value !== 'online') this.check(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.state$.value !== 'online') this.check();
    });
  }
  track<T>(source: Observable<HttpEvent<T>>): Observable<HttpEvent<T>> {
    const started = this.generation;
    return source.pipe(tap({
      next: event => { if (event instanceof HttpResponse && started === this.generation && navigator.onLine) this.update('online'); },
      error: error => {
        if (error?.status === 0 || [502, 503, 504].includes(error?.status)) {
          this.generation++;
          this.update(navigator.onLine ? 'unreachable' : 'offline');
          this.schedule();
        }
      }
    }));
  }
  check(): void {
    if (this.checking) return;
    if (!navigator.onLine) { this.update('offline'); return; }
    this.checking = true;
    const started = this.generation;
    this.http.get(environment.API_BASE_URL + '/player/getLogLevel', { responseType: 'text', params: { reachability: String(Date.now()) } })
      .pipe(timeout(8000)).subscribe({
        next: () => { if (started === this.generation) this.update('online'); },
        error: () => { this.checking = false; this.update(navigator.onLine ? 'unreachable' : 'offline'); this.schedule(); },
        complete: () => { this.checking = false; if (this.state$.value !== 'online') this.schedule(); }
      });
  }
  private schedule(): void {
    if (this.timer || this.state$.value === 'online') return;
    this.timer = setTimeout(() => { this.timer = null; if (document.visibilityState === 'visible') this.check(); else this.schedule(); }, 10000);
  }
  private update(state: 'online' | 'offline' | 'unreachable'): void {
    this.zone.run(() => {
      const previous = this.state$.value;
      if (previous !== state) this.state$.next(state);
      if (state === 'online') {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        if (previous !== 'online') this.recovered$.next();
      }
    });
  }
}
